import React, { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { Check, Clock, X } from 'tabler-icons-react'
import { getExperienceStoreOrderStatus } from '../services/actions/experienceStoreActions'
import { getContrastTextColor } from './myExperienceUtils'
import './ExperienceStorePage.css'

const ExperienceStoreConfirmationPage = () => {
	const { orgSlug, experienceSlug } = useParams()
	const [searchParams] = useSearchParams()
	const orderId = searchParams.get('orderId') || ''
	const sessionId = searchParams.get('session_id') || ''
	const [order, setOrder] = useState(null)
	const [error, setError] = useState('')

	useEffect(() => {
		if (!orderId || !sessionId) {
			setError('This confirmation link is incomplete.')
			return undefined
		}

		let cancelled = false
		let timer = null
		let attempts = 0
		let activeController = null

		const checkStatus = async () => {
			activeController = new AbortController()
			try {
				const result = await getExperienceStoreOrderStatus(
					orgSlug,
					experienceSlug,
					orderId,
					sessionId,
					activeController.signal,
				)
				if (cancelled) return
				const nextOrder = result?.order || null
				setOrder(nextOrder)
				if (nextOrder?.status === 'processing') {
					attempts += 1
					timer = window.setTimeout(checkStatus, attempts < 20 ? 1800 : 10000)
				}
			} catch (statusError) {
				if (!cancelled && statusError.name !== 'AbortError') {
					setError(statusError.message || 'We could not confirm this payment.')
				}
			}
		}

		checkStatus()
		return () => {
			cancelled = true
			if (timer) window.clearTimeout(timer)
			activeController?.abort()
		}
	}, [experienceSlug, orderId, orgSlug, sessionId])

	const storePath = `/${orgSlug}/experiences/${experienceSlug}/store`
	const accentColor = order?.experiencePrimaryColor || '#176b5b'
	const statusPageStyle = {
		'--store-accent': accentColor,
		'--store-accent-text': getContrastTextColor(accentColor),
	}

	if (error) {
		return (
			<div className='experience-store-status-page' style={statusPageStyle}>
				<X size={42} />
				<h1>We couldn’t confirm your purchase</h1>
				<p>{error}</p>
				<Link className='experience-store-confirmation-link secondary' to={storePath}>
					Return to store
				</Link>
			</div>
		)
	}

	if (order?.status === 'paid' && order.checkInId) {
		return (
			<div className='experience-store-status-page' style={statusPageStyle}>
				<div className='experience-store-confirmation-icon success'>
					<Check size={36} />
				</div>
				<h1>You’re all set</h1>
				<p>
					Payment was successful and your check-in has been created. Open your
					pass before heading to check-in.
				</p>
				<div className='experience-store-confirmation-actions'>
					<Link
						className='experience-store-confirmation-link'
						to={`/get-pass/${order.checkInId}`}
					>
						View my pass
					</Link>
					<Link
						className='experience-store-confirmation-link secondary'
						to={`/myexperience/${order.checkInId}`}
					>
						Experience Portal
					</Link>
				</div>
			</div>
		)
	}

	if (['failed', 'expired'].includes(order?.status)) {
		return (
			<div className='experience-store-status-page' style={statusPageStyle}>
				<X size={42} />
				<h1>Payment not completed</h1>
				<p>Your check-in was not created. Return to the store to try again.</p>
				<Link className='experience-store-confirmation-link secondary' to={storePath}>
					Return to store
				</Link>
			</div>
		)
	}

	return (
		<div className='experience-store-status-page' style={statusPageStyle}>
			<div className='experience-store-confirmation-icon pending'>
				<Clock size={34} />
			</div>
			<h1>Finalizing your check-in</h1>
			<p>
				Your payment is being confirmed. Keep this page open; your pass will be
				ready in a moment.
			</p>
		</div>
	)
}

export default ExperienceStoreConfirmationPage
